import type React from 'react';
import { useState } from 'react';
import { UserPlus, Users } from 'lucide-react';
import styled from '@emotion/styled';
import {
  PageHeader, PageTitle, Card, Table, Thead, Tbody,
  PrimaryButton, SecondaryButton, DestructiveButton, Select, Badge,
  ModalOverlay, ModalDialog, ModalTitle, ModalActions,
  FormGroup, Input,
} from '../styled-components';
import { ConfirmModal, EmptyState, SkeletonRows } from '../components';
import { useFetchData } from '../hooks';
import { showToast } from '../hooks';
import { fetchOrThrow, getCSRFToken, CSRF_HEADER_NAME } from '../../common/utils';

interface TeamEntry {
  type: 'member' | 'invite';
  id: string;
  user_id?: string;
  email: string;
  display_name?: string;
  role: string;
  status: string;
  joined_at?: string | null;
  invited_at?: string | null;
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Invite modal
// ---------------------------------------------------------------------------

function InviteModal({ onClose, onInvited }: { onClose: () => void; onInvited: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('developer');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await fetchOrThrow('/dashboard/api/team/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({ email, role }),
      });
      showToast(`Invite sent to ${email}`, 'success');
      onInvited(); onClose();
    } catch { showToast('Failed to send invite', 'error'); }
    finally { setLoading(false); }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalDialog onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Invite Team Member</ModalTitle>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <label>Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus placeholder="colleague@company.com" />
          </FormGroup>
          <FormGroup>
            <label>Role</label>
            <Select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="admin">Admin</option>
              <option value="developer">Developer</option>
              <option value="viewer">Viewer</option>
            </Select>
          </FormGroup>
          <ModalActions>
            <SecondaryButton type="button" onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading || !email}>
              {loading ? 'Sending…' : 'Send Invite'}
            </PrimaryButton>
          </ModalActions>
        </form>
      </ModalDialog>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------------
// Role permissions table
// ---------------------------------------------------------------------------

const PermTable = styled.table({
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
  '& th': { textAlign: 'left', padding: '8px 12px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--bg-border)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' },
  '& td': { padding: '8px 12px', borderBottom: '1px solid var(--bg-border)', color: 'var(--text-primary)' },
  '& tr:last-child td': { borderBottom: 'none' },
});

const checkmark = (b: boolean) => b
  ? <span style={{ color: 'var(--accent-green)' }}>✓</span>
  : <span style={{ color: 'var(--text-muted)' }}>—</span>;

function PermissionsTable() {
  const rows = [
    ['View API Keys', true, true, false],
    ['Create / Deactivate API Keys', true, true, false],
    ['View Phone Numbers', true, true, false],
    ['Buy / Release Numbers', true, true, false],
    ['View Conversations', true, true, true],
    ['Play Recording / View Transcript', true, true, true],
    ['View Campaigns', true, true, true],
    ['Pause / Resume Campaigns', true, true, false],
    ['View Team', true, false, false],
    ['Invite / Manage Team', true, false, false],
    ['Edit Org Settings', true, false, false],
    ['Edit Own Profile', true, true, true],
  ];

  return (
    <PermTable>
      <thead><tr><th>Feature</th><th>Admin</th><th>Developer</th><th>Viewer</th></tr></thead>
      <tbody>
        {rows.map(([label, admin, dev, viewer]) => (
          <tr key={String(label)}>
            <td>{label}</td>
            <td>{checkmark(Boolean(admin))}</td>
            <td>{checkmark(Boolean(dev))}</td>
            <td>{checkmark(Boolean(viewer))}</td>
          </tr>
        ))}
      </tbody>
    </PermTable>
  );
}

// ---------------------------------------------------------------------------
// Team page
// ---------------------------------------------------------------------------

export function TeamPage({ currentUserId }: { currentUserId?: string }) {
  const { data, loading, refetch } = useFetchData<TeamEntry[]>('/dashboard/api/team', []);
  const [showInvite, setShowInvite] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState<TeamEntry | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [lastAdminWarning, setLastAdminWarning] = useState<{ entry: TeamEntry; update: Record<string, string> } | null>(null);

  async function updateMember(userId: string, updates: Record<string, string>, opts?: { warn?: boolean }) {
    try {
      const res = await fetchOrThrow(`/dashboard/api/team/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify(updates),
      });
      const result = await res.json();
      if (result.warning === 'last_admin' && !opts?.warn) {
        const entry = data.find((e) => e.user_id === userId);
        if (entry) setLastAdminWarning({ entry, update: updates });
        return;
      }
      refetch();
    } catch { showToast('Failed to update member', 'error'); }
  }

  async function handleDeactivate() {
    if (!confirmDeactivate?.user_id) return;
    setDeactivating(true);
    await updateMember(confirmDeactivate.user_id, { status: 'deactivated' });
    setDeactivating(false);
    setConfirmDeactivate(null);
    refetch();
  }

  async function resendInvite(inviteId: string) {
    try {
      await fetchOrThrow(`/dashboard/api/team/invite/${inviteId}/resend`, {
        method: 'POST', headers: { [CSRF_HEADER_NAME]: await getCSRFToken() },
      });
      showToast('Invite resent', 'success');
    } catch { showToast('Failed to resend', 'error'); }
  }

  return (
    <div>
      <PageHeader>
        <PageTitle>Team</PageTitle>
        <PrimaryButton onClick={() => setShowInvite(true)}><UserPlus size={16} /> Invite Member</PrimaryButton>
      </PageHeader>

      <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
        <Table>
          <Thead>
            <tr><th>Email</th><th>Role</th><th>Status</th><th>Joined</th><th></th></tr>
          </Thead>
          <Tbody>
            {loading ? (
              <SkeletonRows cols={5} />
            ) : data.length === 0 ? (
              <tr><td colSpan={5}>
                <EmptyState icon={<Users size={40} />} title="No team members" body="Invite your team to collaborate on Guava." />
              </td></tr>
            ) : data.map((entry) => (
              <tr key={entry.id}>
                <td>
                  {entry.email}
                  {entry.user_id === currentUserId && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>(You)</span>
                  )}
                </td>
                <td>
                  {entry.type === 'member' ? (
                    <Select
                      value={entry.role}
                      style={{ width: 120 }}
                      onChange={(e) => updateMember(entry.user_id!, { role: e.target.value })}
                    >
                      <option value="admin">Admin</option>
                      <option value="developer">Developer</option>
                      <option value="viewer">Viewer</option>
                    </Select>
                  ) : (
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{entry.role}</span>
                  )}
                </td>
                <td><Badge variant={entry.status}>{entry.status}</Badge></td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  {entry.type === 'member' ? formatDate(entry.joined_at) : formatDate(entry.invited_at)}
                </td>
                <td onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {entry.type === 'invite' && (
                    <SecondaryButton style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => resendInvite(entry.id)}>
                      Resend
                    </SecondaryButton>
                  )}
                  {entry.type === 'member' && entry.status === 'active' && (
                    <DestructiveButton style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setConfirmDeactivate(entry)}>
                      Deactivate
                    </DestructiveButton>
                  )}
                </td>
              </tr>
            ))}
          </Tbody>
        </Table>
      </Card>

      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 12 }}>
          Role Permissions
        </div>
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <PermissionsTable />
        </Card>
      </div>

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onInvited={refetch} />}

      {confirmDeactivate && (
        <ConfirmModal
          title={`Deactivate ${confirmDeactivate.email}?`}
          body="This user will immediately lose access to the dashboard."
          confirmLabel="Deactivate" destructive loading={deactivating}
          onConfirm={handleDeactivate} onCancel={() => setConfirmDeactivate(null)}
        />
      )}

      {lastAdminWarning && (
        <ConfirmModal
          title="You are the only Admin"
          body="If you proceed, no one will be able to manage team settings until Gridspace support intervenes. Are you sure?"
          confirmLabel="Proceed anyway"
          loading={false}
          onConfirm={() => {
            updateMember(lastAdminWarning.entry.user_id!, lastAdminWarning.update, { warn: true });
            setLastAdminWarning(null);
          }}
          onCancel={() => setLastAdminWarning(null)}
        />
      )}
    </div>
  );
}
